import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { reviewApprovalSaga } from './saga';
import { ReviewApprovalState } from './types';
import {
  MyApprovalsQueryParams,
  MyApprovalsResponse,
  MyHistoryQueryParams,
  MyHistoryResponse,
  MyApprovalRequestsQueryParams,
  MyApprovalRequestsResponse
} from '../types';

export const initialState: ReviewApprovalState = {
  quickEditRevision: 0,
  myApprovals: { data: null, loading: false, error: null },
  myHistory: { data: null, loading: false, error: null },
  myApprovalRequests: { data: null, loading: false, error: null }
};

/**
 * Own slice/saga for the three Review & Approval dashboard GETs - request/
 * success/failure action triplet per resource, same shape Home's slice uses
 * for its own page-mount fetches (see Home/slice/index.ts). Kept separate
 * from `home` since none of this data is Home's own concern. The dashboard
 * consumes its own scoped Quick Edit completion rather than Home's
 * timeline-highlight alert.
 */
const slice = createSlice({
  name: 'reviewApproval',
  initialState,
  reducers: {
    quickEditSaved(state) {
      state.quickEditRevision += 1;
    },
    loadMyApprovals(state, action: PayloadAction<MyApprovalsQueryParams>) {
      state.myApprovals.loading = true;
      state.myApprovals.error = null;
    },
    myApprovalsLoaded(state, action: PayloadAction<MyApprovalsResponse>) {
      state.myApprovals.data = action.payload;
      state.myApprovals.loading = false;
    },
    loadMyApprovalsError(state, action: PayloadAction<any>) {
      state.myApprovals.error = action.payload;
      state.myApprovals.loading = false;
    },

    loadMyHistory(state, action: PayloadAction<MyHistoryQueryParams>) {
      state.myHistory.loading = true;
      state.myHistory.error = null;
    },
    myHistoryLoaded(state, action: PayloadAction<MyHistoryResponse>) {
      state.myHistory.data = action.payload;
      state.myHistory.loading = false;
    },
    loadMyHistoryError(state, action: PayloadAction<any>) {
      state.myHistory.error = action.payload;
      state.myHistory.loading = false;
    },

    // `my-reviews` has no documented sort/filter query params yet (see
    // ../types.ts) - just page/size, the same pagination query every
    // dashboard table's fetch now carries.
    loadMyApprovalRequests(
      state,
      action: PayloadAction<MyApprovalRequestsQueryParams>
    ) {
      state.myApprovalRequests.loading = true;
      state.myApprovalRequests.error = null;
    },
    myApprovalRequestsLoaded(
      state,
      action: PayloadAction<MyApprovalRequestsResponse>
    ) {
      state.myApprovalRequests.data = action.payload;
      state.myApprovalRequests.loading = false;
    },
    loadMyApprovalRequestsError(state, action: PayloadAction<any>) {
      state.myApprovalRequests.error = action.payload;
      state.myApprovalRequests.loading = false;
    }
  }
});

export const { actions: reviewApprovalActions, reducer } = slice;

export const useReviewApprovalSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: reviewApprovalSaga });
  return { actions: slice.actions };
};
